import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { ADMIN_FEEDBACK_OWNER_ID } from '../threads/types';
import type { Thread, ThreadStatus, AppFeedbackKind } from '../threads/types';
import type { ThreadApi } from '../threads/threadApi';
import { ThreadStatusBadge } from '../threads/ThreadStatusBadge';
import { Button, DataTable, LoadingSpinner, SearchFilterBar, formatDate, formatMonthDaySlashTime24 } from '../components';
import type { ColumnDef, FilterDef, FilterOption, SortState } from '../components';
import { AppFeedbackForm } from './AppFeedbackForm';
import { AppFeedbackDetail } from './AppFeedbackDetail';
import type { AppFeedbackAdminMode } from './AppFeedbackDetail';
import {
    getFeedbackKind,
    getSubmitterName,
    getSubmitterRole,
    getUpdatedAt,
    isUnreadByCreator,
    isUnreadByOwnerTeam,
} from './appFeedbackHelpers';
import './AppFeedbackPage.css';

export interface AppFeedbackPageAdminMode {
    /** Update feedback status */
    updateStatus: (thread: Thread, status: ThreadStatus) => Promise<Thread>;
    /** Save edits to kind / title / description / status on a thread (single explicit save). */
    saveFeedback?: (thread: Thread, changes: { feedbackKind?: AppFeedbackKind; title?: string; description?: string; status?: ThreadStatus }) => Promise<Thread>;
    /** Add admin message (server figures out partition from ownerId). */
    addMessage: (thread: Thread, message: string, opts?: { showAsPopup?: boolean }) => Promise<Thread>;
    /** Edit own admin message on another user's feedback (uses /console route). */
    editMessage?: (thread: Thread, messageId: string, message: string) => Promise<Thread>;
    /** Reassign feedback to a different user by email */
    reassign?: (thread: Thread, email: string) => Promise<Thread>;
    /** Render extra content in detail view (e.g. submission info block) */
    renderDetailExtra?: (thread: Thread) => React.ReactNode;
    /**
     * List source for the table. Defaults to the page's `api` (user-scope
     * `/threads`, the caller's own tenant); the admin console passes its
     * `/console/threads` client so the tenant filter can narrow the read.
     */
    listApi?: ThreadApi;
    /** Tenant filter options. Omit to hide the filter (single-tenant admins). */
    tenantOptions?: FilterOption[];
    /** The user picker for the "From" filter (the app owns user search). Its
     *  value is an email or partial email; it applies on Fetch like the rest. */
    renderUserFilter?: (value: string, onChange: (email: string) => void) => ReactNode;
}

interface AppFeedbackPageProps {
    api: ThreadApi;
    /** Optional: navigate to a linked object (submission, question, etc.) */
    onNavigateObject?: (objectType: string, objectId: string) => void;
    /** Passed through to the detail view — see AppFeedbackDetail. */
    canNavigateObject?: (objectType: string) => boolean;
    /**
     * Logged-in viewer's userId. When provided, enables:
     *   - Edit button on the viewer's own feedback (within 24h, not resolved/closed)
     *   - Edit button on the viewer's own reply messages
     * If omitted, existing content is read-only.
     */
    currentUserId?: string;
    /** Admin mode: shows From column, filters, status control, meta info */
    adminMode?: AppFeedbackPageAdminMode;
    /**
     * Open this thread's detail once the list has loaded (e.g. `?thread=` from
     * the feedback-reply pop-up). Fetched directly if it isn't on the first page.
     */
    initialThreadId?: string;
    /** List heading on the self view. Defaults to "Feedback". */
    title?: string;
    /**
     * Read-only view of OTHER people's feedback (e.g. a teacher viewing their
     * students'). Supplies its own list source (server-scoped to what the viewer
     * may see); renders the From column and a read-only detail — no "New
     * Feedback" button, no reply box. Mutually exclusive with adminMode.
     */
    readOnlyMode?: {
        listThreads: (opts: { sortBy: 'userName' | 'updatedAt' | 'createdAt'; sortOrder: 'asc' | 'desc' }) => Promise<{ items: Thread[]; total: number }>;
        /** Page heading. Defaults to "Feedback". */
        title?: string;
        /** Empty-state line. Defaults to "No feedback found." */
        emptyText?: string;
    };
}

type View = 'list' | 'new' | 'detail';

const TYPE_LABELS: Record<string, string> = {
    bug: 'Bug',
    suggestion: 'Suggestion',
    flag: 'Flag',
    survey: 'Survey',
};

const OBJECT_TYPE_LABELS: Record<string, string> = {
    submission: 'Submission',
    question: 'Question',
    hint: 'Hint',
};

const STATUS_OPTIONS: ThreadStatus[] = ['open', 'in_progress', 'resolved', 'wont_fix', 'closed'];
const STATUS_LABELS: Record<ThreadStatus, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    replied: 'Replied',
    resolved: 'Resolved',
    wont_fix: "Won't Fix",
    closed: 'Closed',
};

/** Map the page's sortable column keys to threadApi's sort keys. */
type SortColumn = 'from' | 'created' | 'updated';
const SORT_KEY_MAP: Record<SortColumn, 'userName' | 'createdAt' | 'updatedAt'> = {
    from: 'userName',
    created: 'createdAt',
    updated: 'updatedAt',
};

export function AppFeedbackPage({ api, onNavigateObject, canNavigateObject, currentUserId, adminMode, readOnlyMode, initialThreadId, title }: AppFeedbackPageProps) {
    // From-column table (admin + read-only) vs. the compact self-view table.
    const tableMode = !!adminMode || !!readOnlyMode;
    const [view, setView] = useState<View>('list');
    const [items, setItems] = useState<Thread[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Admin filters — COMMITTED values only. They change when Fetch is pressed
    // (SearchFilterBar holds the drafts), never per keystroke. The user picker
    // lives outside the bar, so its draft is held here and committed on Fetch.
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [userDraft, setUserDraft] = useState('');
    const [sort, setSort] = useState<SortState>({ sortBy: 'updated', sortOrder: 'desc' });
    // The spinner replaces the page only on the first load; after that the filter
    // bar stays mounted (it holds the drafts) and the table just refreshes.
    const loadedOnce = useRef(false);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const listApi = adminMode?.listApi ?? api;
            const result = readOnlyMode
                ? await readOnlyMode.listThreads({
                    sortBy: SORT_KEY_MAP[sort.sortBy as SortColumn],
                    sortOrder: sort.sortOrder,
                })
                : await listApi.list({
                    ownerId: ADMIN_FEEDBACK_OWNER_ID,
                    type: 'app_feedback',
                    ...(adminMode ? {} : { createdBy: 'me' }),
                    feedbackKind: filters.kind ? (filters.kind as AppFeedbackKind) : undefined,
                    status: filters.status || undefined,
                    objectType: filters.objectType || undefined,
                    email: filters.email?.trim() || undefined,
                    tenantId: filters.tenant || undefined,
                    sortBy: SORT_KEY_MAP[sort.sortBy as SortColumn],
                    sortOrder: sort.sortOrder,
                });
            setItems(result.items);
            setTotal(result.total);
        } catch {
            // silently fail
        } finally {
            loadedOnce.current = true;
            setLoading(false);
        }
    }, [api, adminMode, readOnlyMode, filters, sort]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    // Deep link: open the requested thread once, after the first load.
    const openedThreadRef = useRef<string | null>(null);
    useEffect(() => {
        if (!initialThreadId || loading || openedThreadRef.current === initialThreadId) return;
        openedThreadRef.current = initialThreadId;
        const open = (thread: Thread) => {
            setItems((prev) => (prev.some((i) => i.id === thread.id) ? prev : [thread, ...prev]));
            setSelectedId(thread.id);
            setView('detail');
        };
        const listed = items.find((i) => i.id === initialThreadId);
        if (listed) open(listed);
        else api.get(initialThreadId, ADMIN_FEEDBACK_OWNER_ID).then(open).catch(() => { /* stay on the list */ });
    }, [initialThreadId, loading, items, api]);

    const handleSubmitted = () => {
        setView('list');
        loadItems();
    };

    const handleItemUpdated = (updated: Thread) => {
        setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    };

    const selectedItem = items.find((item) => item.id === selectedId);
    // A read-only observer isn't a participant, so nothing is "unread" for them.
    const isUnread = (item: Thread) => readOnlyMode ? false : adminMode ? isUnreadByOwnerTeam(item) : isUnreadByCreator(item);

    if (loading && !loadedOnce.current) {
        return (
            <div className="feedback-page">
                <LoadingSpinner />
            </div>
        );
    }

    if (view === 'new') {
        return (
            <div className="feedback-page">
                <h1>New Feedback</h1>
                <AppFeedbackForm api={api} onSubmitted={handleSubmitted} onCancel={() => setView('list')} showOnBehalfOf={!!adminMode} />
            </div>
        );
    }

    if (view === 'detail' && selectedItem) {
        const adminDetailMode: AppFeedbackAdminMode | undefined = adminMode ? {
            updateStatus: (status) => adminMode.updateStatus(selectedItem, status).then(u => { handleItemUpdated(u); return u; }),
            saveFeedback: adminMode.saveFeedback ? (changes) => adminMode.saveFeedback!(selectedItem, changes).then(u => { handleItemUpdated(u); return u; }) : undefined,
            addMessage: (msg, opts) => adminMode.addMessage(selectedItem, msg, opts).then(u => { handleItemUpdated(u); return u; }),
            editMessage: adminMode.editMessage ? (mid, msg) => adminMode.editMessage!(selectedItem, mid, msg).then(u => { handleItemUpdated(u); return u; }) : undefined,
            reassign: adminMode.reassign ? (email) => adminMode.reassign!(selectedItem, email).then(u => { handleItemUpdated(u); return u; }) : undefined,
            renderDetailExtra: adminMode.renderDetailExtra,
        } : undefined;
        return (
            <div className="feedback-page">
                <AppFeedbackDetail
                    thread={selectedItem}
                    api={api}
                    onUpdated={handleItemUpdated}
                    onBack={() => { setView('list'); setSelectedId(null); }}
                    onNavigateObject={onNavigateObject}
                    canNavigateObject={canNavigateObject}
                    currentUserId={readOnlyMode ? undefined : currentUserId}
                    adminMode={adminDetailMode}
                    readOnly={!!readOnlyMode}
                />
            </div>
        );
    }

    const hasObjects = items.some((item) => item.objectType && item.objectId);
    // The bar's own filters — `email` belongs to the user picker beside it.
    const { email: _email, ...barValues } = filters;

    return (
        <div className="feedback-page">
            <div className="feedback-page-header">
                <h1>{readOnlyMode?.title ?? (adminMode ? 'User Feedback' : (title ?? 'Feedback'))}</h1>
                {!readOnlyMode && <Button onClick={() => setView('new')}>New Feedback</Button>}
            </div>

            {adminMode && (
                <div className="feedback-filters">
                    <SearchFilterBar
                        filters={buildAdminFilters(adminMode.tenantOptions)}
                        values={barValues}
                        hideSearch
                        // Fetch (and Clear) commit the bar's drafts; the user picker
                        // sits outside the bar, so its draft is committed alongside.
                        onChange={(next) => setFilters({ ...next, email: userDraft })}
                        leading={adminMode.renderUserFilter?.(userDraft, setUserDraft)}
                        loading={loading}
                    />
                    <span className="feedback-count">{total} items</span>
                </div>
            )}

            {items.length === 0 ? (
                <div className="feedback-empty">
                    <p>{readOnlyMode ? (readOnlyMode.emptyText ?? 'No feedback found.') : adminMode ? 'No feedback found.' : 'No feedback submitted yet.'}</p>
                    {!adminMode && !readOnlyMode && <p>Have a bug to report or a suggestion? Click "New Feedback" above.</p>}
                </div>
            ) : tableMode ? (
                <DataTable<Thread>
                    compact
                    data={items}
                    rowKey={(r) => r.id}
                    onRowClick={(r) => { setSelectedId(r.id); setView('detail'); }}
                    rowClassName={(r) => isUnread(r) ? 'dt-row--unread' : ''}
                    sort={sort}
                    onSortChange={setSort}
                    columns={buildAdminColumns(hasObjects, isUnread, !adminMode)}
                    columnToggleKey={adminMode ? 'admin-user-feedback' : 'feedback-read-only'}
                />
            ) : (
                <div className="dt-scroll">
                  <table className="dt dt--compact feedback-table">
                      <thead>
                          <tr>
                              <th>Type</th>
                              <th>Title</th>
                              {hasObjects && <th>Object</th>}
                              <th>Status</th>
                              <th>Updated</th>
                          </tr>
                      </thead>
                      <tbody>
                          {items.map((item) => {
                              const kind = getFeedbackKind(item);
                              return (
                                  <tr
                                      key={item.id}
                                      className={`dt-clickable ${isUnread(item) ? 'dt-row--unread' : ''}`}
                                      onClick={() => { setSelectedId(item.id); setView('detail'); }}
                                  >
                                      <td>
                                          {kind && (
                                              <span className={`feedback-type-label feedback-type-${kind}`}>
                                                  {TYPE_LABELS[kind] || kind}
                                              </span>
                                          )}
                                      </td>
                                      <td className="feedback-title-cell">
                                          {item.title}
                                          {isUnread(item) && <span className="feedback-unread-dot" />}
                                      </td>
                                      {hasObjects && (
                                          <td>
                                              {item.objectType ? (
                                                  <span className="feedback-object-cell">
                                                      {OBJECT_TYPE_LABELS[item.objectType] || item.objectType}
                                                  </span>
                                              ) : '—'}
                                          </td>
                                      )}
                                      <td><ThreadStatusBadge status={item.status} /></td>
                                      <td>{formatMonthDaySlashTime24(getUpdatedAt(item))}</td>
                                  </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            )}

            {!adminMode && !readOnlyMode && (
                <div className="feedback-contact">
                    You can also reach us at <a href="mailto:support@buzzbrain.ai">support@buzzbrain.ai</a>
                </div>
            )}
        </div>
    );
}

/** Admin-mode column definitions for the feedback DataTable. */
/** The admin filter bar. Applied on Fetch (SearchFilterBar), not per keystroke. */
function buildAdminFilters(tenantOptions?: FilterOption[]): FilterDef[] {
    const defs: FilterDef[] = [
        { key: 'kind', placeholder: 'All Types', options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })) },
        { key: 'status', placeholder: 'All Statuses', options: STATUS_OPTIONS.map((st) => ({ value: st, label: STATUS_LABELS[st] })) },
        { key: 'objectType', placeholder: 'All Objects', options: Object.entries(OBJECT_TYPE_LABELS).map(([value, label]) => ({ value, label })) },
    ];
    if (tenantOptions && tenantOptions.length > 0) {
        defs.push({ key: 'tenant', placeholder: 'All Tenants', options: tenantOptions });
    }
    return defs;
}

/** `compact` — the read-only view a teacher gets of their students' feedback:
 *  From first, one date column (Updated, with the time) and no reply count.
 *  Admins keep the full table. */
function buildAdminColumns(hasObjects: boolean, isUnread: (row: Thread) => boolean, compact = false): ColumnDef<Thread>[] {
    const cols: ColumnDef<Thread>[] = [
        {
            key: 'type',
            header: 'Type',
            render: (r) => {
                const kind = getFeedbackKind(r);
                return kind ? (
                    <span className={`feedback-type-label feedback-type-${kind}`}>
                        {TYPE_LABELS[kind] || kind}
                    </span>
                ) : null;
            },
        },
        {
            key: 'title',
            header: 'Title',
            className: 'feedback-title-cell',
            hideable: false,
            render: (r) => (
                <>
                    {r.title}
                    {isUnread(r) && <span className="feedback-unread-dot" />}
                </>
            ),
        },
    ];
    if (hasObjects) {
        cols.push({
            key: 'object',
            header: 'Object',
            render: (r) => r.objectType ? (
                <span className="feedback-object-cell">{OBJECT_TYPE_LABELS[r.objectType] || r.objectType}</span>
            ) : '—',
        });
    }
    cols.push(
        {
            key: 'from',
            header: 'From',
            sortable: true,
            render: (r) => (
                <>
                    {getSubmitterName(r)}
                    <span className="feedback-from-role"> ({getSubmitterRole(r)})</span>
                </>
            ),
        },
        { key: 'status', header: 'Status', render: (r) => <ThreadStatusBadge status={r.status} /> },
        ...(compact ? [] : [
            { key: 'replies', header: 'Replies', render: (r: Thread) => r.messages.length },
            { key: 'created', header: 'Created on', sortable: true, render: (r: Thread) => formatDate(r.createdAt) },
        ]),
        {
            key: 'updated', header: 'Updated', sortable: true,
            render: (r) => compact ? formatMonthDaySlashTime24(getUpdatedAt(r)) : formatDate(getUpdatedAt(r)),
        },
    );
    // The teacher is reading their students' feedback: who sent it leads the row.
    if (compact) {
        const from = cols.findIndex(c => c.key === 'from');
        cols.unshift(...cols.splice(from, 1));
    }
    return cols;
}
